import clsx from 'clsx';
import React, {
  ReactElement, ReactNode, useRef,useState
} from 'react';
import { createPortal } from 'react-dom';

import styles from './tooltip.module.scss';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

type TooltipProps = {
  children: ReactNode;
  content: ReactNode;
  position?: TooltipPosition;
  className?: string;
  disabled?: boolean;
};

export function Tooltip( {
  children,
  content,
  position = 'top',
  className,
  disabled = false
}: TooltipProps ): ReactElement {
  const [ showTooltip, setShowTooltip ] = useState( false );
  const [ tooltipPos, setTooltipPos ] = useState( { x: 0, y: 0 } );
  const elementRef = useRef<HTMLDivElement>( null );

  const calculatePosition = ( rect: DOMRect, position: TooltipPosition ) => {
    switch ( position ) {
      case 'top':
        return {
          x: rect.left + rect.width / 2,
          y: rect.top
        };
      case 'bottom':
        return {
          x: rect.left + rect.width / 2,
          y: rect.bottom
        };
      case 'left':
        return {
          x: rect.left,
          y: rect.top + rect.height / 2
        };
      case 'right':
        return {
          x: rect.right,
          y: rect.top + rect.height / 2
        };
      default:
        return {
          x: rect.left + rect.width / 2,
          y: rect.top
        };
    }
  };

  const handleMouseEnter = () => {
    if ( disabled || !content ) return;

    if ( elementRef.current ) {
      const rect = elementRef.current.getBoundingClientRect();
      setTooltipPos( calculatePosition( rect, position ) );
      setShowTooltip( true );
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip( false );
  };

  return (
    <>
      <div
        className={ clsx( styles.tooltip_wrapper, className ) }
        onMouseEnter={ handleMouseEnter }
        onMouseLeave={ handleMouseLeave }
        ref={ elementRef }
      >
        { children }
      </div>
      {
        showTooltip && !disabled && content && typeof document !== 'undefined' && createPortal(
          <div
            className={ clsx( styles.tooltip_portal, styles[ `tooltip_${ position }` ] ) }
            style={ {
              left: `${ tooltipPos.x }px`,
              top: `${ tooltipPos.y }px`
            } }
          >
            { content }
          </div>,
          document.body
        )
      }
    </>
  );
}
